import type { WorkflowClientCommandReference } from './workflow-command-contracts';

/** Installed, versioned providers interpret these settings; authored JSON never executes code. */
export interface WorkflowCompletionRequirement {
  id: string;
  provider: string;
  version: number;
  settingsVersion: number;
  settingsHash?: string | null;
  settings: Record<string, unknown>;
}

export interface WorkflowCompletionRequirementCheck {
  id: string;
  provider: string;
  label: string;
  state: string;
  reasonCode: string;
  message: string;
  evidence: {
    sourceType: string;
    sourceId: string | number;
    sourceRevision: string | number;
  }[];
  upload?: { allowedFormats: string[]; maximumBytes: number } | null;
}

export interface WorkflowRequirementProvider {
  provider: string;
  version: number;
  label: string;
  description: string;
  settingsVersion: number;
  settingsHash: string;
  sourceType: string;
  settings: {
    name: string;
    label: string;
    type: string;
    required: boolean;
    maxLength?: number | null;
    choices?: { value: string; label: string }[] | null;
  }[];
}

export interface WorkflowCompletionChecklist {
  occurrenceId: number;
  canComplete: boolean;
  requirements: WorkflowCompletionRequirementCheck[];
}

export interface WorkflowDocumentUploadReceipt {
  receiptId: number;
  occurrenceId: number;
  requirementId: string;
  eTag: string;
  scanReceiptId: string;
  scanStatus: 'clean';
}

/** Scan clearance and attachment are one confirmed server result; selection alone is never evidence. */
export function isCleanWorkflowDocumentUploadReceipt(
  value: WorkflowDocumentUploadReceipt,
  occurrenceId: number,
  requirementId: string,
): boolean {
  return (
    !!value &&
    Number.isSafeInteger(value.receiptId) &&
    value.receiptId > 0 &&
    value.occurrenceId === occurrenceId &&
    value.requirementId === requirementId &&
    typeof value.eTag === 'string' &&
    !!value.eTag.trim() &&
    value.scanStatus === 'clean' &&
    typeof value.scanReceiptId === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value.scanReceiptId,
    ) &&
    value.scanReceiptId !== '00000000-0000-0000-0000-000000000000'
  );
}

export interface WorkflowDecisionRequirementSettings {
  sourceCommand: WorkflowClientCommandReference;
  decisionKind: string;
  outcomeCodes: string[];
}

export const completionProviders = {
  form: 'workflow.form-completed',
  document: 'workflow.document-upload',
  decision: 'workflow.decision-recorded',
  quorum: 'workflow.quorum-evaluated',
  notices: 'notices.work-completed',
  votingPopulation: 'voting.population-confirmed',
  votingRound: 'voting.round-completed',
  hearingDecision: 'hearing.decision-finalized',
  hearingConduct: 'hearing.conduct-completed',
  hearingGrantReview: 'hearing.grant-review-completed',
  sdmf: 'sdmf.completed',
  schedulingRequested: 'scheduling.requested',
  hearingScheduled: 'scheduling.booked',
} as const;
